import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { useSelector } from "react-redux";
import { useGetAdministeredOrganizationsQuery } from "api/account/accountApiSlice";
import { AdministeredOrganization } from "api/account/types";
import { RootState } from "../../../redux/store";
import { makeOrganizationMembersPath } from "../common/organizationAdmin";

/**
 * "Organizations you manage" — the entry point to the account's second scope
 * (#2163). Rendered only for someone holding an approved `owner` membership,
 * which for almost everyone means it never appears at all.
 *
 * IT RENDERS FROM THE ENDPOINT, NOT FROM THE PROFILE. The tempting shortcut
 * is `profile.memberships.some(m => m.role === 'owner' && m.status ===
 * 'approved')` — the data is already there and it costs no request. It is
 * wrong: `approvedMemberships` on the backend filters on status alone and
 * does NOT exclude archived organizations, so that expression stays true for
 * an organization the tenant has closed, and the section would link to a page
 * the server correctly 404s. The endpoint applies the same filter the write
 * paths do, so it is the only answer that agrees with them.
 *
 * The profile is used only as a CHEAP PRE-CHECK to decide whether to make the
 * request at all — never to decide what the person may do.
 */
const OrganizationAdminSection: React.FC = () => {
  const { t } = useTranslation();
  const profile = useSelector((state: RootState) => state.account.profile);

  // Pre-check only (see the header): skips the request for the overwhelming
  // majority of accounts, and is never treated as the answer.
  const mightAdminister = Boolean(
    profile?.memberships?.some(
      (membership) => membership.role === "owner" && membership.status === "approved"
    )
  );

  const { data, isError } = useGetAdministeredOrganizationsQuery(undefined, {
    skip: !mightAdminister,
  });

  // Shape-check rather than `?? []`: an error body or an unexpected shape is
  // truthy and would sail past a fallback, and `.map` on it takes the whole
  // account page down (house rule after the `pricing_discounts = {}` runs).
  const organizations: AdministeredOrganization[] = Array.isArray(data) ? data : [];

  if (!mightAdminister) return null;

  // An error must not look like "you manage nothing" — a silent empty state
  // is how a broken read goes unreported (#1579/#1581).
  if (isError) {
    return (
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-slate-700">
          {t("account.organization_admin.title")}
        </h2>
        <p className="text-sm text-slate-600">{t("account.organization_admin.load_error")}</p>
      </div>
    );
  }

  if (organizations.length === 0) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-medium text-slate-700">
        {t("account.organization_admin.title")}
      </h2>
      <p className="text-sm text-slate-600">{t("account.organization_admin.intro")}</p>

      {/* A list from the start: an account can hold the mark in more than one
          organization (the uniqueness index is per organization + account), and
          a single-value layout would have to be rebuilt the first time one
          does. */}
      <ul className="space-y-2">
        {organizations.map((organization) => (
          <li key={organization.organizationId}>
            <Link
              to={makeOrganizationMembersPath(organization.organizationId)}
              className="flex items-center justify-between gap-3 rounded-md bg-primary/5 px-3 py-2 text-sm text-foreground"
            >
              <span>{organization.organizationName}</span>
              <span className="flex items-center gap-2 text-slate-600">
                <Users className="h-4 w-4" />
                {t("account.organization_admin.manage_members")}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OrganizationAdminSection;
